import React, { useState, useRef, useEffect, useMemo } from "react";
import "./Dropdown.scss";
import { ReactComponent as DropDownIcon } from 'img/dropdown.svg';
import ReactDOM from 'react-dom';
import { SETTING_CONTENTS_NAMES } from "config/settingsConfig";

interface Option {
  value: string;
  label: string;
}

export interface DropdownProps {
  id?: string;
  placeholder?: string;
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  position?: 'top' | 'bottom';
}

function Dropdown({
  id,
  placeholder = "Select an option",
  options,
  value,
  onChange,
  position = 'bottom',
  className = "",
}: DropdownProps): React.JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);
  const fontValues = Object.values(SETTING_CONTENTS_NAMES.general.fonts) as string[];
  const isFontOption = options.length > 0 && options.every((opt) => fontValues.includes(opt.value));
  const toFontClassName = (fontName: string) => `force-font-${fontName.replace(/\s+/g, '')}`;

  const handleClose = ()=>{
    return setIsOpen(false);
  };

  // 선택된 옵션은 맨위로 보이게끔 설정
  const displayOptions = useMemo(() => {
    if (!value) return options;
    const selected = options.find(opt => opt.value === value);
    if (!selected) return options;
    return [ ...options.filter(opt => opt.value !== value)];
  }, [options, value]);

  // esc로 드롭다운 닫기
  useEffect(() => {
    if(!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // event.preventDefault();
        event.stopPropagation();
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown,true); // register capturing
    return () => window.removeEventListener('keydown', handleKeyDown,true);// unregister capturing
  }, [isOpen]);

  // 아웃사이드 닫기
  useEffect(() => {
    if(!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const updatePosition = () => {
      if (dropdownRef.current) {
        const rect = dropdownRef.current.getBoundingClientRect();
        setCoords({
          top: rect.bottom + 2,
          left: rect.left,
          width: rect.width,
        });
      }
    };
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen]);

  return (
    <div ref={dropdownRef} className={`custom-dropdown ${className}`} id={id}>
      <div
        className={`dropdown-header ${value ? 'filled' : 'empty'}`}
        onClick={() => setIsOpen(!isOpen)}
        >
        <span
          className={`dropdown-placeholder ${isFontOption && selectedOption ? toFontClassName(selectedOption.value) : ''}`}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <span className={`arrow ${isOpen ? "open" : ""}`}>
          <DropDownIcon className={`input-icon-color`} />
        </span>
      </div>
      {isOpen && (
        <ul className={`dropdown-list position-${position}`}>
          {displayOptions.map(opt => (
            <li
              key={opt.value}
              className={`
                ${isFontOption ? `customFont-dropdown-item ${toFontClassName(opt.value)}` : 'dropdown-item'}
                ${value === opt.value ? 'selected' : ''}
              `}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Dropdown;
